import { useScroll } from '@react-spring/web';
import cx from 'classnames';
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type FC,
  type MouseEvent,
} from 'react';

import { Icon } from 'src/components-v2/DataDisplay';
import { Column, Container, Row } from 'src/components-v2/Layout';
import PlaidLogo from 'src/components-v2/SVGs/PlaidLogo';
import { Link } from 'src/components/Inputs';
import { useDeviceSize } from 'src/hooks';

import { Dialog } from './Dialog';
import { NavCtaButtons } from './NavCtaButtons';
import { NavList } from './NavList';

import { DEFAULT_THEME } from './consts';
import { navData } from './data';

import { usePageLocale } from 'src/contexts';
import styles from './MainNavigationBar.module.scss';
import { useForeignContentNotification } from 'src/components/Feedback/ForeignContentNotification/ForeignContentNotification';
import { NEXT_PUBLIC_PERCY_RUN, NEXT_PUBLIC_IS_PREVIEW } from 'src/env';
import { useMainTemplateContext } from 'src/contexts/MainTemplateContext/MainTemplateContext';
import type { NavigationVariant } from 'src/lib/site-navigation';

type MainNavigationBarProps = {
  skipLinkAnchor?: string;
  variant?: NavigationVariant;
};

/** Web 3.0 header. All pages and locales should eventually use this. */
// A shared element lets React skip this icon when the nav re-renders right
// after hydration, before the icon's lazy chunk has loaded. A fresh element
// would update its Suspense boundary there and make React throw #421.
const MENU_ICON = <Icon icon='Menu' sx={{ width: 22, height: 22 }} />;

export const MainNavigationBar: FC<MainNavigationBarProps> = ({
  skipLinkAnchor = '#main-content',
  variant = 'default',
}) => {
  // COMMON
  const isMinimal = variant === 'minimal';
  const pageLocale = usePageLocale();
  const { items, ctaTrackingIds } = navData[pageLocale];
  const { isNewMainNavDesktop } = useDeviceSize();
  const { isForeignContentNotificationOpen } = useForeignContentNotification();
  const { isNavigationHidden } = useMainTemplateContext();
  const [isMounted, setIsMounted] = useState(false);
  const navRef = useRef<HTMLElement>(null);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  // SCROLL
  const [scrollDirection, setScrollDirection] = useState<'up' | 'down'>('up');
  const prevScrollY = useRef(0);

  const isVisible = !isNavigationHidden && scrollDirection === 'up';

  useScroll({
    onChange: ({ value: { scrollY } }) => {
      const legalNav = document.querySelector('#docsSubnav');
      const sideNav = document.querySelector('#desktopSidenavMenu');
      if (scrollY > 100) {
        const direction = scrollY > prevScrollY.current ? 'down' : 'up';
        setScrollDirection(direction);
        prevScrollY.current = scrollY;

        // we modify the position of the legacy legal navigation and side
        // nav in order to support the web 3 navigation on legacy legal pages
        // while this it is not ideal to mutate dom nodes without a ref this
        // is the cheapest solution without a significant refactor
        // legal docs are probably one of the oldest sections on plaid.com
        // and still uses jQuery and Foundation so not following react paradigms
        // should be acceptable for now
        if (direction === 'down') {
          legalNav?.classList.remove('slide-down');
          legalNav?.classList.add('slide-up');
          sideNav?.classList.remove('slide-down');
          sideNav?.classList.add('slide-up');
        } else {
          legalNav?.classList.remove('slide-up');
          legalNav?.classList.add('slide-down');
          sideNav?.classList.remove('slide-up');
          sideNav?.classList.add('slide-down');
        }
      } else {
        legalNav?.classList.add('slide-down');
        sideNav?.classList.add('slide-down');
      }
    },
  });

  // MODAL
  const [isOpen, setOpen] = useState(false);
  const dialogRef = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    if (typeof document === 'undefined') return;
    // The minimal variant has no dialog, so it must not touch body styles
    if (isMinimal) return;

    if (isOpen) {
      document.body.style.overflowY = 'hidden';
      document.body.style.height = 'initial';
    } else {
      document.body.style.overflowY = 'visible';
      document.body.style.height = '100%';
    }

    // Restore body scroll if the nav unmounts or switches variant while the
    // dialog is open (e.g. a Site Navigation change in live preview)
    return () => {
      document.body.style.overflowY = 'visible';
      document.body.style.height = '100%';
    };
  }, [isOpen, isMinimal]);

  // Update react state when native dialog is closed with escape key or the
  // Android back gesture to keep body scroll in sync
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    const handleNativeClose = () => {
      setOpen(false);
    };

    dialog.addEventListener('close', handleNativeClose);
    return () => {
      dialog.removeEventListener('close', handleNativeClose);
    };
  }, [isMinimal]);

  const handleOpenDialog = () => {
    if (dialogRef.current) {
      setOpen(true);
      dialogRef.current.showModal();
    }
  };

  const handleCloseDialog = useCallback(() => {
    dialogRef.current?.close();
  }, []);

  // SMOOTH SCROLL
  const handleScrollTo = (event: MouseEvent<HTMLAnchorElement>) => {
    const id = event.currentTarget.getAttribute('href');
    const targetElement = document.querySelector(id) as HTMLElement;

    if (!targetElement) return;

    event.preventDefault();

    const prefersReducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)',
    ).matches;
    const navHeight = navRef.current ? navRef.current.clientHeight : 0;
    const top =
      targetElement.getBoundingClientRect().top + window.scrollY - navHeight;

    window.scrollTo({
      top,
      behavior: prefersReducedMotion ? 'auto' : 'smooth',
    });

    // focus() without preventScroll cancels the smooth scroll above
    const focusableElements = targetElement.querySelectorAll<HTMLElement>(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
    );

    if (focusableElements.length > 0) {
      focusableElements[0].focus({ preventScroll: true });
    }
  };

  const logoLink = (
    <Link href='/'>
      <a aria-label='Go to Plaid homepage' className={styles.Logo}>
        <PlaidLogo
          sx={{
            display: 'block',
            height: { xs: '24px', sm: '28px' },
            color: '#111',
          }}
        />
      </a>
    </Link>
  );

  return (
    <nav
      id='web3-nav'
      aria-label='Main Navigation'
      ref={navRef}
      className={cx(styles.Root, styles[`Root_${DEFAULT_THEME}`], {
        [styles['Root_show-by-scroll']]: isVisible,
        [styles['Root_hide-by-scroll']]: !isVisible,
        [styles['Root_foreign-content-notification-open']]:
          isForeignContentNotificationOpen &&
          !NEXT_PUBLIC_PERCY_RUN &&
          !NEXT_PUBLIC_IS_PREVIEW,
      })}
    >
      <Container className={styles.Root__container}>
        <Row>
          <Column xs={24} lgOffset={2} lg={20}>
            <div className={styles.Root__bar}>
              <div className={styles.Root__inner}>
                <a
                  href={skipLinkAnchor}
                  className={styles['Root__link-to-content']}
                  onClick={handleScrollTo}
                >
                  Skip to main content
                </a>

                {isMinimal ? (
                  <>
                    {logoLink}

                    <NavCtaButtons variant='minimal' />
                  </>
                ) : isMounted && isNewMainNavDesktop ? (
                  <>
                    <NavList items={items}>{logoLink}</NavList>

                    <NavCtaButtons />
                  </>
                ) : (
                  <>
                    {logoLink}

                    <button
                      type='button'
                      className={styles.Burger}
                      tabIndex={0}
                      aria-label='Main menu'
                      aria-expanded={isOpen}
                      onClick={handleOpenDialog}
                    >
                      {MENU_ICON}
                    </button>
                  </>
                )}
              </div>
            </div>
          </Column>
        </Row>
      </Container>

      {!isMinimal && (
        <Dialog
          className={styles.Root__dialog}
          ref={dialogRef}
          items={items}
          onClose={handleCloseDialog}
          trackingIds={ctaTrackingIds}
        />
      )}
    </nav>
  );
};
